import React, { useEffect, useState } from 'react'

import { useFormikContext } from 'formik'

import FormNavigationButtons from './FormNavigationButtons'
import { IpsaMoneyField, IpsaNumberField } from './Form/IpsaFormattedFields'
import IpsaSelectField from './Form/IpsaSelectField'
import IpsaTextField from './Form/IpsaTextField'
import { Protection } from '../../../variables/InitialValues'
import styled from 'styled-components'
import { GetBlob, SaveBlob } from '../../../store/BlobsApiClient'

const HeaderContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-content: center;

    #text-field-header {
        font-size: 24px;
    }

    #text-field-button {
        margin: auto;
    }
`

const InputContainer = styled.div`
    display: flex;
    align-items: center;
`

const DetailedFields = ({ formProtectionKey, value }) => (
    <>
        <InputContainer className="col-xs-12">
            <IpsaMoneyField
                name={`protections.${formProtectionKey}.benefitAmount`}
                type="text"
                label="Monthly Benefit Amount"
                informationText="This is the monthly benefit amount that the client would receive if they were totally disabled."
            />
        </InputContainer>
        <InputContainer className="col-xs-12">
            <IpsaMoneyField
                name={`protections.${formProtectionKey}.monthlyMaximum`}
                type="text"
                label="Monthly Maximum / Cap"
                informationText="This is the maximum monthly benefit the carrier can go up to."
            />
        </InputContainer>
        <InputContainer className="col-xs-12">
            <IpsaSelectField
                values={[2, 5, 10, 65, 67, 70]}
                name={`protections.${formProtectionKey}.benefitPeriod`}
                label="Benefit Period"
                placeholder="Select Benefit Period"
                informationText="This is age or time frame the client would receive benefits."
            />
        </InputContainer>
        <InputContainer className="col-xs-12">
            <IpsaSelectField
                values={['0', '30', '60', '90', '180', '365']}
                name={`protections.${formProtectionKey}.eliminationPeriod`}
                label="Elimination Period"
                placeholder="Select Elimination Period"
                informationText="Also known as the waiting period, is the number of days following the start of disability before benefits become payable."
            />
        </InputContainer>
        <InputContainer className="col-xs-12">
            <IpsaSelectField
                values={['0%', '1%', '2%', '3%', '4%', '5%', '6%']}
                name={`protections.${formProtectionKey}.cola`}
                label="COLA %"
                placeholder="Select COLA %"
                informationText="Which is short for Cost-of-Living Adjustment, helps the monthly benefit amount keep pace with inflation while the client is on claim and receiving benefits."
            />
        </InputContainer>
        <InputContainer className="col-xs-12">
            <IpsaSelectField
                values={['0%', '1%', '2%', '3%', '4%', '5%', '6%']}
                name={`protections.${formProtectionKey}.annualBenefitIncrease`}
                label="Annual Benefit Increase %"
                placeholder="Select Annual Benefit Increase %"
                informationText="Each carrier has their own version of this provision.  This feature helps the benefit keep pace with inflation and increasing income while the client is working and not on claim."
            />
        </InputContainer>
        <InputContainer className="col-xs-12">
            <IpsaNumberField
                name={`protections.${formProtectionKey}.annualBenefitIncreaseMaxAge`}
                type="text"
                label="Max Age for Annual Benefit Increase"
                informationText="Each carrier will have a maximum age the benefit will increase to.  Some carriers state it in years (typically 5 or 6 years from policy issue date) or until a maximum age, which is typically age 55."
            />
        </InputContainer>

        <InputContainer className="col-xs-12">
            <IpsaMoneyField
                name={`protections.${formProtectionKey}.catastrophicBenefitAmount`}
                type="text"
                label="Monthly Catastrophic Benefit Amount"
                informationText="This provides an additional monthly benefit if the client were to become severely disabled and unable to perform two or more of the six activities of daily living (ADLs).  Each carrier will have their own version of this rider."
            />
        </InputContainer>
        <InputContainer className="col-xs-12">
            <IpsaMoneyField
                name={`protections.${formProtectionKey}.monthlyPremium`}
                type="text"
                label="Monthly Premium"
                informationText="Enter the estimated monthly premium amount here.  Round to the nearest dollar.  If annual premium, divide by 12."
            />
        </InputContainer>
        <InputContainer className="col-xs-12">
            <IpsaSelectField
                values={['Yes', 'No']}
                name={`protections.${formProtectionKey}.taxableBenefits`}
                label="Taxable Benefit"
                placeholder="Select are Benefits Taxable?"
                informationText="Most individual DI premiums are paid with after-tax dollars, so the benefits would be non-taxable.  However, in certain circumstances, if the employer pays the premium, the benefits may be taxable."
            />
        </InputContainer>
    </>
)

const ProtectionLevels = ['Comprehensive', 'Moderate', 'Basic']

const IncomeProtectionAnalysisStep = ({ clientId, onSave }) => {
    const { values, setFieldValue, setTouched } = useFormikContext()
    const { protections } = values
    const [showInputs, setShowInputs] = useState({})

    const show = (level) => {
        const pro = protections[level.toLowerCase()]
        return showInputs[level] || (pro.benefitPeriod !== '' && pro.benefitAmount !== '0')
    }

    const GetFileName = (cd) => {
        var filename = ''
        var disposition = cd
        if (disposition && disposition.indexOf('attachment') !== -1) {
            var filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
            var matches = filenameRegex.exec(disposition)
            if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '')
            }
        }

        return filename
    }

    const DownloadBlob = async (id) => {
        const returnstream = await GetBlob(id)

        const type = returnstream.headers['content-type']
        const cd = returnstream.headers['content-disposition']
        const fileName = GetFileName(cd)

        const blob = new Blob([returnstream.data], { type: type, encoding: 'UTF-8' })
        const link = document.createElement('a')

        link.href = window.URL.createObjectURL(blob)
        link.download = fileName

        link.click()
    }

    const SaveClientProtectionBlob = async (file, level) => {
        const blobId = await SaveBlob(file)
        if (blobId && blobId.data) {
            setFieldValue(`protections.${level.toLowerCase()}.blobId`, blobId.data)
            setTouched(`protections.${level.toLowerCase()}.blobId`)
        }
    }

    const DeleteClientProtectionBlob = async (blobId, level) => {
        if (blobId) {
            setFieldValue(`protections.${level.toLowerCase()}.blobId`, '')
            setTouched(`protections.${level.toLowerCase()}.blobId`)
            // delete on save
            // values.protections[level.toLowerCase()].blobId = ''
            // await onSave(values)
            // await DeleteBlob(blobId)
        }
    }

    useEffect(() => {
        // on load if we are not showing a set of inputs then we should set all the fields
        // to the empty protection level object. This will make the showing of the inputs
        // consistent.
        for (let idx = 0; idx < ProtectionLevels.length; idx++) {
            const level = ProtectionLevels[idx]
            if (!show(level)) {
                ResetLevel(level)
            }
        }
    }, [])

    const ResetLevel = (level) => {
        setFieldValue('protections.' + level.toLowerCase(), Protection)
        setFieldValue('protections.' + level.toLowerCase() + '.name', level)
        setShowInputs((_) => ({
            ..._,
            [level]: false,
        }))
    }

    return (
        <>
            <div id="new-client-1" className="new-client-form animated-form">
                <div className="col-section col-no-margin">
                    {ProtectionLevels.map((level) => (
                        <div key={level} className="col-xs-12 col-md-4">
                            <div className="col-section col-no-margin">
                                <HeaderContainer className="col-xs-12">
                                    <div style={{ minWidth: '15px' }}></div>
                                    <IpsaTextField
                                        name={`protections.${level.toLowerCase()}.name`}
                                        type="text"
                                        label="Plan Name"
                                        id="text-field-header"
                                    />
                                    <div className="text-field-button">
                                        {show(level) ? (
                                            <button
                                                className="btn btn-right"
                                                type="button"
                                                onClick={() => {
                                                    ResetLevel(level)
                                                }}
                                            >
                                                x
                                            </button>
                                        ) : (
                                            <button
                                                className="btn btn-right"
                                                type="button"
                                                onClick={() => {
                                                    setShowInputs((_) => ({
                                                        ..._,
                                                        [level]: true,
                                                    }))
                                                }}
                                                style={{ marginLeft: '20px' }}
                                            >
                                                +
                                            </button>
                                        )}
                                    </div>
                                </HeaderContainer>
                                {show(level) && (
                                    <>
                                        <DetailedFields
                                            formProtectionKey={level.toLowerCase()}
                                            value={protections[level.toLowerCase()]}
                                        />
                                    </>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <FormNavigationButtons
                section="protections"
                previous="assets"
                clientId={clientId}
                onSave={onSave}
            />
        </>
    )
}

export default IncomeProtectionAnalysisStep
